import { captureException } from "@sentry/react";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";

import { useCall } from "@/providers/hooks/use-call";
import { Call } from "@/types/call.types";
import { getBrandFromOrgId } from "@/utils/get-brand-from-org-id";

/** If the call was created more than this long ago, show a confirmation before starting. */
const STALE_CALL_THRESHOLD_SECONDS = 30;

function isCallStale(createdAt: string | undefined): boolean {
    if (!createdAt) return false;
    return DateTime.now().diff(DateTime.fromISO(createdAt), "seconds").seconds > STALE_CALL_THRESHOLD_SECONDS;
}

function startCallWithBrand(
    callData: Call,
    startCall: ReturnType<typeof useCall>["startCall"],
    setSelectedBrand: ReturnType<typeof useCall>["setSelectedBrand"]
): void {
    if (callData.orgId) {
        const brandFromOrgId = getBrandFromOrgId(callData.orgId);
        if (brandFromOrgId) {
            setSelectedBrand(brandFromOrgId);
        }
    } else {
        captureException(new Error("Couldn't change brand. No orgId found in call data" + JSON.stringify(callData)));
    }

    startCall(callData.calledNumber, {
        callId: callData.id,
        orgId: callData.orgId,
        userId: callData.userId,
        moduleType: callData.moduleType,
        recordId: callData.recordId,
    });
}

/**
 * Hook for handling call notifications
 * Manages call notification logic including brand changes and call initiation.
 * Shows a confirmation dialog when the call was created more than 1 minute ago (e.g. delayed FCM).
 */
export const useCallNotification = () => {
    const { startCall, setSelectedBrand } = useCall();
    const [pendingStaleCall, setPendingStaleCall] = useState<Call | null>(null);

    /**
     * Handles call notification data.
     * If the call was created more than 1 minute ago, shows a confirmation dialog instead of starting immediately.
     */
    const handleCallNotification = useCallback(
        (callData: Call): void => {
            if (isCallStale(callData.createdAt)) {
                setPendingStaleCall(callData);
                return;
            }

            startCallWithBrand(callData, startCall, setSelectedBrand);
        },
        [startCall, setSelectedBrand]
    );

    const confirmStaleCall = useCallback(() => {
        if (!pendingStaleCall) return;
        startCallWithBrand(pendingStaleCall, startCall, setSelectedBrand);
        setPendingStaleCall(null);
    }, [pendingStaleCall, startCall, setSelectedBrand]);

    const dismissStaleCall = useCallback(() => {
        setPendingStaleCall(null);
    }, []);

    return {
        handleCallNotification,
        pendingStaleCall,
        confirmStaleCall,
        dismissStaleCall,
    };
};
